<template>
  <div class="news-list-section">
    <div class="container">
      <!-- 筛选器 -->
      <div class="news-filter">
        <div class="filter-buttons">
          <div
            v-for="item in navItems"
            :key="item.id"
            class="filter-btn"
            :class="{ active: activeCategory === item.value }"
            @click="handleCategoryChange(item.value)"
          >
            {{ item.label }}
          </div>
        </div>
      </div>

      <!-- 空状态提示 -->
      <div v-if="paginatedNews.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <h3>暂无相关内容</h3>
        <p>该分类下暂时没有新闻内容，敬请期待</p>
      </div>

      <!-- 新闻列表 -->
      <div v-else class="news-grid">
        <div class="news-card" v-for="item in paginatedNews" :key="item.id">
          <div class="news-image">
            <img
              class="image-placeholder"
              v-if="item.image"
              :src="item.image"
              alt="新闻图片"
            />
            <div class="image-placeholder" v-else="item.image">新闻图片</div>
          </div>
          <div class="news-content">
            <div class="news-category">{{ item.category }}</div>
            <h3>{{ item.title }}</h3>
            <p class="news-date">{{ item.date }}</p>
            <p class="news-excerpt">{{ item.excerpt }}</p>
            <div class="news-meta">
              <span class="meta-item">👁️ {{ item.views }} 浏览</span>
              <span class="meta-item">💬 {{ item.comments }} 评论</span>
            </div>
            <div class="news-link" @click="handleViewDetail(item)">
              阅读全文
            </div>
          </div>
        </div>
      </div>

      <!-- 分页组件 -->
      <Pagination
        :total="filteredNews.length"
        :page-size="pageSize"
        :current-page="currentPage"
        @change="handlePageChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Pagination from "./Pagination.vue";
import type { NewsType } from "@/stores/news";
import { scrollToTop } from "@/utils/scrollTo";

interface NavItem {
  id: number;
  label: string;
  value: string;
}

const props = defineProps<{
  newsList: NewsType[];
}>();

// Emits 定义
const emit = defineEmits<{
  viewDetail: [item: NewsType];
}>();

// 导航项
const navItems: NavItem[] = [
  { id: 0, label: "全部", value: "all" },
  { id: 1, label: "医院动态", value: "hospitalNews" },
  { id: 2, label: "医疗服务", value: "medicalServices" },
  { id: 3, label: "信息公告", value: "information" },
  { id: 4, label: "健康科普", value: "healthKnowledge" },
  { id: 5, label: "学术建设", value: "academicBuilding" },
];

// 当前选中的分类
const activeCategory = ref("all");

// 处理分类切换
const handleCategoryChange = (value: string) => {
  scrollToTop();

  activeCategory.value = value;
};

// 根据选中分类过滤新闻
const filteredNews = computed(() => {
  if (activeCategory.value === "all") {
    return props.newsList;
  }

  // 根据 category 字段进行模糊匹配
  return props.newsList.filter((item) => {
    const categoryMap: Record<string, string[]> = {
      hospitalNews: ["医院新闻", "门诊排班", "医院动态"],
      medicalServices: ["医疗技术", "荣誉奖项"],
      information: ["通知", "招聘信息"],
      healthKnowledge: ["健康讲座"],
      academicBuilding: ["学术", "科研"],
    };

    const keywords = categoryMap[activeCategory.value] || [];
    return keywords.some((keyword) => item.category.includes(keyword));
  });
});

// 分页相关
const currentPage = ref(1);
const pageSize = 4; // 每页显示 6 条新闻

// 当前页的新闻数据
const paginatedNews = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  const end = start + pageSize;
  return filteredNews.value.slice(start, end);
});

// 处理分页变化
const handlePageChange = (page: number) => {
  currentPage.value = page;
  scrollToTop();
};

// 查看详情
const handleViewDetail = (item: NewsType) => {
  emit("viewDetail", item);
};

// 监听分类变化，重置页码
watch(activeCategory, () => {
  currentPage.value = 1;
});
</script>

<style scoped>
/* 内容区域通用样式 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-header h2 {
  font-size: 2.5rem;
  color: #1a5fb4;
  margin-bottom: 15px;
}

.section-header p {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

/* 新闻列表 */
.news-list-section {
  padding: 80px 0;
  background-color: #f8f9fa;
}

.news-filter {
  margin-bottom: 40px;
  /* background: white; */
  padding: 25px 30px;
  /* border-radius: 12px; */
  /* box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06); */
}

.filter-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 10px;
}

.filter-btn {
  padding: 10px 20px;
  border: 2px solid #e0e7ff;
  background: white;
  color: #5a6b8c;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  position: relative;
  overflow: hidden;
}

.filter-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a5fb4 0%, #2a8df8 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.filter-btn:hover {
  border-color: #1a5fb4;
  color: #1a5fb4;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(26, 95, 180, 0.15);
}

.filter-btn.active {
  border-color: #1a5fb4;
  background: linear-gradient(135deg, #1a5fb4 0%, #2a8df8 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(26, 95, 180, 0.3);
  transform: translateY(-2px);
}

.filter-btn.active::before {
  opacity: 1;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-bottom: 50px;
}

/* 空状态样式 */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  color: #666;
}

.empty-icon {
  font-size: 5rem;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #999;
  margin-bottom: 10px;
}

.empty-state p {
  font-size: 1rem;
  color: #bbb;
}

.news-card {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s,
    box-shadow 0.3s;
}

.news-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.news-image {
  height: 200px;
  background: linear-gradient(135deg, #1a5fb4, #2a8df8);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.news-content {
  padding: 25px;
}

.news-category {
  display: inline-block;
  padding: 5px 15px;
  background: #1a5fb4;
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  margin-bottom: 15px;
}

.news-card h3 {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
}

.news-date {
  color: #999;
  font-size: 0.9rem;
  margin-bottom: 15px;
}

.news-excerpt {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;

  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.news-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  color: #888;
  font-size: 0.9rem;
}

.news-link {
  color: #1a5fb4;
  text-decoration: none;
  font-weight: 600;
  display: inline-block;
  transition: color 0.3s;
}

.news-link:hover {
  color: #0d4a9e;
  text-decoration: underline;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.page-btn {
  padding: 10px 25px;
  border: 2px solid #1a5fb4;
  background: white;
  color: #1a5fb4;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  background: #1a5fb4;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 10px;
}

.page-number {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .news-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .news-list-section {
    padding: 60px 0;
  }
}

@media (max-width: 768px) {
  .news-list-section {
    padding: 50px 0;
  }
  
  .container {
    padding: 0 15px;
  }
  
  .news-filter {
    padding: 20px 15px;
    margin-bottom: 30px;
  }

  .filter-buttons {
    gap: 8px;
    row-gap: 8px;
  }

  .filter-btn {
    padding: 8px 14px;
    font-size: 0.9rem;
  }
  
  .news-card h3 {
    font-size: 1.2rem;
  }
  
  .news-content {
    padding: 20px;
  }
  
  .news-image {
    height: 180px;
  }
  
  .empty-state {
    padding: 60px 20px;
  }
  
  .empty-icon {
    font-size: 4rem;
  }
  
  .empty-state h3 {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .news-list-section {
    padding: 40px 0;
  }
  
  .container {
    padding: 0 10px;
  }
  
  .news-filter {
    padding: 15px 10px;
  }
  
  .filter-btn {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
  
  .news-card h3 {
    font-size: 1.1rem;
  }
  
  .news-content {
    padding: 18px;
  }
  
  .news-image {
    height: 160px;
  }
  
  .news-excerpt {
    font-size: 0.85rem;
  }
  
  .news-meta {
    gap: 15px;
    font-size: 0.85rem;
  }
  
  .empty-state {
    padding: 50px 15px;
  }
  
  .empty-icon {
    font-size: 3.5rem;
  }
}
</style>